import type { RigListItem, RigTrailer, RigTruck } from '@chukua/common';
import { Button, Typography } from 'antd';
import { formatCouplingTime } from '../policies/coupling-labels';
import { RigSilhouette } from './RigSilhouette';

interface RigCardProps {
  /** Whose page this is: a unit's (the truck's) or a spare trailer's. */
  vehicleKind: 'truck' | 'trailer';
  rig: RigListItem;
  canManage: boolean;
  onCouple: () => void;
  onUncouple: () => void;
}

function CoupledFacts({ vehicleKind, rig, truck, trailer }: Pick<RigCardProps, 'vehicleKind' | 'rig'> & { truck: RigTruck; trailer: RigTrailer }) {
  const isTruck = vehicleKind === 'truck';
  const other = isTruck ? trailer : truck;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted">{isTruck ? 'Pulling' : 'Coupled to'}</dt>
      <dd>
        <Typography.Text code>{other.registration}</Typography.Text>{' '}
        <span className="text-muted">{isTruck ? (trailer.typeName ?? trailer.typeCode) : (truck.className ?? truck.classCode)}</span>
      </dd>
      <dt className="text-muted">Since</dt>
      <dd>{rig.coupledAt ? formatCouplingTime(rig.coupledAt) : '—'}</dd>
    </dl>
  );
}

function ParkedFacts({ rig }: { rig: RigListItem }) {
  if (!rig.parkedAt) return <p className="m-0 text-sm">Not yet coupled to any truck.</p>;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted">Parked at</dt>
      <dd>{rig.parkedAt.zoneName ?? rig.parkedAt.zoneCode}</dd>
      <dt className="text-muted">Since</dt>
      <dd>{formatCouplingTime(rig.parkedAt.since)}</dd>
    </dl>
  );
}

function Facts({ vehicleKind, rig }: Pick<RigCardProps, 'vehicleKind' | 'rig'>) {
  const { truck, trailer } = rig;
  if (truck && trailer) return <CoupledFacts vehicleKind={vehicleKind} rig={rig} truck={truck} trailer={trailer} />;
  if (!truck) return <ParkedFacts rig={rig} />;
  const text = truck.coupling === 'none' ? `A ${truck.className ?? truck.classCode} doesn't tow a trailer.` : 'No trailer coupled.';
  return <p className="m-0 text-sm">{text}</p>;
}

function Actions({ vehicleKind, rig, canManage, onCouple, onUncouple }: RigCardProps) {
  if (!canManage) return null;
  if (rig.kind === 'coupled') {
    return (
      <div className="flex flex-wrap gap-2">
        {vehicleKind === 'truck' && <Button onClick={onCouple}>Swap trailer</Button>}
        <Button onClick={onUncouple}>Uncouple</Button>
      </div>
    );
  }
  if (rig.truck?.coupling === 'none') return null;
  return (
    <div className="flex flex-wrap gap-2">
      <Button type="primary" onClick={onCouple}>
        {vehicleKind === 'truck' ? 'Couple a trailer' : 'Couple to a truck'}
      </Button>
    </div>
  );
}

/** The rig a truck or trailer is part of, drawn, with couple, swap and uncouple beside it (gitaumoses4/chukua#390). */
export function RigCard(props: RigCardProps) {
  const { rig } = props;
  const label = [rig.truck?.registration, rig.trailer?.registration].filter(Boolean).join(' with ');
  return (
    <section className="mb-4 grid gap-5 rounded-lg border border-line bg-surface p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)] lg:items-center">
      <RigSilhouette truck={rig.truck} trailer={rig.trailer} label={label} className="h-24 w-full" />
      <div className="flex flex-col gap-3">
        <Facts vehicleKind={props.vehicleKind} rig={rig} />
        <Actions {...props} />
      </div>
    </section>
  );
}
